/**
 * Financial periods: a "month" that need not run 1st-to-last-of-month. Mirrors the server's
 * app/core/dates.py exactly (same tests, same worked examples: 26 Aug - 25 Sep), because the
 * app has to navigate and label periods (the Prev/Next chevrons, a fresh page's default URL)
 * before any request has come back - see that module's docstring for why 28 is the cap.
 *
 * Dates here are plain "YYYY-MM-DD" strings (as the API sends and receives them), never a JS
 * Date used for arithmetic - see lib/format.ts's parseDay for why (no timezone to get wrong).
 */

export const MIN_CYCLE_START_DAY = 1;
export const MAX_CYCLE_START_DAY = 28;
export const DEFAULT_CYCLE_START_DAY = 1;

/** How many days a calendar month has (1-indexed month, i.e. January = 1). */
function daysInMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

function pad(n: number): string {
  return String(n).padStart(2, "0");
}

function toIso(year: number, month: number, day: number): string {
  return `${String(year).padStart(4, "0")}-${pad(month)}-${pad(day)}`;
}

interface Ymd {
  year: number;
  month: number; // 1-12
  day: number;
}

function parse(iso: string): Ymd {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number) as [number, number, number];
  return { year, month, day };
}

function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + (month - 1) + delta;
  const y = Math.floor(index / 12);
  const m = ((index % 12) + 12) % 12;
  return { year: y, month: m + 1 };
}

/** The cycle-start date inside a given calendar month, e.g. the 26th even in a 28-day February. */
function cycleAnchor(year: number, month: number, startDay: number): string {
  return toIso(year, month, Math.min(startDay, daysInMonth(year, month)));
}

function addDays(iso: string, delta: number): string {
  const { year, month, day } = parse(iso);
  const d = new Date(year, month - 1, day + delta);
  return toIso(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/** Start of the financial period that contains `iso`. */
export function cycleStart(iso: string, startDay: number = DEFAULT_CYCLE_START_DAY): string {
  const { year, month } = parse(iso);
  if (startDay <= MIN_CYCLE_START_DAY) return toIso(year, month, 1);
  const anchor = cycleAnchor(year, month, startDay);
  // Both are "YYYY-MM-DD", zero-padded: string order is chronological order.
  if (iso.slice(0, 10) >= anchor) return anchor;
  const prev = shiftMonth(year, month, -1);
  return cycleAnchor(prev.year, prev.month, startDay);
}

/** Last day of the financial period that begins at `periodStart`. */
export function cycleEnd(periodStart: string, startDay: number = DEFAULT_CYCLE_START_DAY): string {
  const { year, month } = parse(periodStart);
  if (startDay <= MIN_CYCLE_START_DAY) return toIso(year, month, daysInMonth(year, month));
  const next = shiftMonth(year, month, 1);
  return addDays(cycleAnchor(next.year, next.month, startDay), -1);
}

/** The period_start `n` financial periods before (`n<0`) or after (`n>0`) this one. */
export function addCycles(
  periodStart: string, n: number, startDay: number = DEFAULT_CYCLE_START_DAY,
): string {
  const { year, month } = parse(periodStart);
  if (startDay <= MIN_CYCLE_START_DAY) {
    const shifted = shiftMonth(year, month, n);
    return toIso(shifted.year, shifted.month, 1);
  }
  const shifted = shiftMonth(year, month, n);
  return cycleAnchor(shifted.year, shifted.month, startDay);
}

export interface Period {
  start: string;
  end: string;
}

export function periodContaining(iso: string, startDay: number = DEFAULT_CYCLE_START_DAY): Period {
  const start = cycleStart(iso, startDay);
  return { start, end: cycleEnd(start, startDay) };
}

/** Whether a period is exactly a calendar month (true for every period when startDay is 1). */
export function isCalendarMonth(period: Period): boolean {
  const { day } = parse(period.start);
  return day === 1 && period.end === cycleEnd(period.start, 1);
}
